import { env } from '../env';
import { loadConfig } from './load';
import { setConfig } from './store';
import { AppConfig } from './types';

function swapFavicon(faviconUrl: string): void {
  try {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  } catch {
    /* best-effort: a bad favicon URL must never blank the page */
  }
}

function injectGoogleAnalytics(measurementId: string): void {
  // Fire-and-forget. googletagmanager.com is heavily ad-blocked; a blocked or
  // failed load must NEVER reject bootstrap or reach the error screen.
  try {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + measurementId;
    document.head.appendChild(script);
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    // Canonical gtag: push the `arguments` object (gtag.js expects that shape;
    // a plain array is not an equivalent substitute). A function EXPRESSION
    // (not a block-scoped declaration) with a rest-param type keeps this
    // compiling under the repo's es5 + strict tsconfig (avoids TS1252/TS2554).
    const gtag = function (..._args: unknown[]): void {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer.push(arguments);
    };
    gtag('js', new Date());
    gtag('config', measurementId);
  } catch {
    /* best-effort */
  }
}

function injectClarity(projectId: string): void {
  // Fire-and-forget, same contract as GA: Clarity injects its own script tag,
  // which is commonly ad-blocked. A blocked/failed load must NEVER reject
  // bootstrap or reach the error screen. Loaded lazily so the ~KBs of tag glue
  // stay out of the critical config path.
  void import('@microsoft/clarity')
    .then(({ default: Clarity }) => {
      try {
        Clarity.init(projectId);
      } catch {
        /* best-effort */
      }
    })
    .catch(() => {
      /* best-effort: chunk load failure must not blank the page */
    });
}

export async function bootstrap(): Promise<AppConfig> {
  const cfg = await loadConfig(); // fetch + validate — the ONLY fatal step
  setConfig(cfg); // deep-freeze + store
  document.title = cfg.tenant.siteTitle;
  swapFavicon(cfg.branding.faviconUrl);
  if (cfg.analytics.gaPropertyId) {
    injectGoogleAnalytics(cfg.analytics.gaPropertyId);
  }
  // Clarity project ID comes from window.env (the tenant's env.js ConfigMap),
  // NOT the backend app-config. Absent/empty => Clarity not loaded.
  const clarityProjectId = env.CLARITY_PROJECT_ID;
  if (typeof clarityProjectId === 'string' && clarityProjectId.trim().length > 0) {
    injectClarity(clarityProjectId.trim());
  }
  return cfg;
}
